<script setup lang="ts">
import { LocaleLink } from '#components'

export type ButtonTypes = 'primary' | 'secondary' | 'danger' | 'success' | 'close' | 'link'

const props = defineProps<{
  label: string
  variant: ButtonTypes
  disabled?: boolean
  to?: string
  title?: string
}>()

/**
 * With `to` set this renders a real <a href> via NuxtLink instead of a <button>
 * that calls router.push. That distinction matters: a router.push button is
 * invisible to crawlers, which is why "Read more" and "Back to blog" links used
 * to contribute nothing to the internal link graph.
 *
 * A disabled button stays a <button> — `disabled` is meaningless on an anchor.
 *
 * The link (LocaleLink, a NuxtLink that keeps the reader's language) is passed
 * as the imported component, not as the string 'NuxtLink':
 * a string is only resolved against locally registered components, so `<component
 * is="NuxtLink">` rendered a literal <NuxtLink> element. Browsers treat that as
 * an unknown inline element, which is why every button with `to` looked fine and
 * did nothing when clicked.
 */
const asLink = computed(() => !!props.to && !props.disabled)
</script>

<template>
  <component
    :is="asLink ? LocaleLink : 'button'"
    :to="asLink ? props.to : undefined"
    :type="asLink ? undefined : 'button'"
    :disabled="asLink ? undefined : props.disabled"
    class="flex w-full cursor-pointer items-center justify-center gap-2 align-middle no-underline"
    :class="[variant, { 'h-12': variant === 'primary' }]"
    :title="props.title ?? props.label"
    :aria-label="props.label"
  >
    <div v-if="$slots['icon-left']" class="size-4">
      <slot name="icon-left" />
    </div>
    <slot>{{ label }}</slot>
    <div v-if="$slots['icon-right']" class="size-4">
      <slot name="icon-right" />
    </div>
  </component>
</template>

<style lang="scss">
:root {
  --primary-gradient: linear-gradient(to right, #9333ea, #7e22ce);
  --primary-gradient-hover: linear-gradient(to right, #7e22ce, #6b21a8);
}

.chat {
  @apply rounded-full
    p-2
    text-sm
    text-gray-900
    transition-all
    duration-200
    focus:border-purple-500
    focus:ring-purple-500
    dark:border-gray-600
    dark:text-white
    dark:placeholder:text-gray-400
    dark:focus:border-purple-500
    dark:focus:ring-purple-500;


  &:hover {
    @apply scale-105;
    background: var(--primary-gradient-hover);
  }
}

.secondary {
  @apply inline-flex items-center justify-center
    rounded-lg
    border border-gray-300
    bg-white
    px-5
    py-2.5
    text-sm font-medium
    text-gray-700
    shadow-sm
    hover:scale-105
    hover:bg-gray-50 hover:text-gray-800 dark:hover:text-gray-300 hover:border-gray-400
    focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white transition-all duration-300
    dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 dark:hover:border-gray-500 dark:focus-visible:ring-green-400 dark:focus-visible:ring-offset-gray-900;

  &:disabled {
    @apply cursor-not-allowed opacity-60 hover:bg-white hover:text-gray-700 hover:border-gray-300 dark:hover:bg-gray-800;
  }

  > i {
    @apply h-4 w-4;
  }
}

.primary {
  @apply
    text-sm
  placeholder:text-gray-400
   rounded-lg
    bg-gradient-to-r
    from-green-500
    to-green-600
    px-6
    py-3
    text-center
    font-bold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-green-600 hover:to-green-700;

  &:hover {
   @apply  bg-green-800
  }

  &:disabled {
    @apply cursor-not-allowed opacity-60 hover:bg-white hover:border-gray-300 dark:hover:bg-gray-800;
  }
}

.danger {
  @apply rounded-md
    bg-red-700
    p-2
    text-sm
    text-white;

  &:hover {
    @apply bg-red-800;
  }

  &.close {
    @apply rounded-md
      bg-white
      p-2
      text-sm
      text-gray-700;
  }

  &.close:hover {
    @apply bg-gray-100;
  }
}

.link {
  @apply text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300;
}
</style>
